import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Search, Upload, Send, X, Trash2, ChevronDown, ChevronRight, Package, Pencil, Undo2 } from "lucide-react";
import { toast } from "sonner";
import ExportExcelButton from "@/components/ExportExcelButton";

const ROLL_SIZE_OPTIONS = [200, 250, 300];

export default function Stock() {
  const { user } = useAuth();
  const role = user?.role;
  const [tab, setTab] = useState("products");
  const [items, setItems] = useState([]);
  const [productsGrouped, setProductsGrouped] = useState([]);
  const [summary, setSummary] = useState({});
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(true);
  const [newBatchOpen, setNewBatchOpen] = useState(false);
  const [allocOpen, setAllocOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [productRolls, setProductRolls] = useState({});
  const [transferRoll, setTransferRoll] = useState(null);
  const [editRoll, setEditRoll] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, byProd, its] = await Promise.all([
        api.get("/stock/summary"),
        api.get("/stock/by-product"),
        api.get(`/stock/items?${state ? `state=${state}&` : ""}${q ? `q=${encodeURIComponent(q)}` : ""}`),
      ]);
      setSummary(s.data);
      setProductsGrouped(byProd.data || []);
      setItems(its.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); api.get("/products").then(r => setProducts(r.data)); }, []);
  useEffect(() => { load(); }, [tab, state]);

  const selectedCodes = Object.keys(selectedItems).filter(k => selectedItems[k]);

  const toggleProduct = async (name) => {
    if (expandedProduct === name) { setExpandedProduct(null); return; }
    setExpandedProduct(name);
    if (!productRolls[name]) {
      try {
        const r = await api.get(`/stock/items?limit=500`);
        const rolls = (r.data || []).filter(x => x.product_name === name);
        setProductRolls(prev => ({ ...prev, [name]: rolls }));
      } catch { /* noop */ }
    }
  };

  const deleteRoll = async (id, ppf_code) => {
    if (!window.confirm(`Delete roll ${ppf_code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/stock/rolls/${id}`);
      toast.success(`Deleted ${ppf_code}`);
      setProductRolls({});
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const deleteMasterProduct = async (name) => {
    if (!window.confirm(`Delete ALL unallocated ${name} rolls at Manufacturer? Allocated/used rolls are skipped.`)) return;
    try {
      const r = await api.delete(`/stock/master-product?product_name=${encodeURIComponent(name)}`);
      toast.success(`Deleted ${r.data.deleted} roll(s) (${r.data.skipped} skipped)`);
      setProductRolls({});
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const unallocateRoll = async (id, ppf_code) => {
    if (!window.confirm(`Recall roll ${ppf_code} back to Manufacturer?`)) return;
    try {
      await api.post(`/stock/rolls/${id}/unallocate`);
      toast.success(`${ppf_code} recalled to Manufacturer`);
      setProductRolls({});
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Unallocate failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Inventory</div>
          <h1 className="text-3xl tracking-tighter font-medium mt-1">Stock</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <ExportExcelButton path="/export/stock" filename="stock.xlsx" testid="export-stock-btn" />
          {role === "manufacturer" && (
            <button data-testid="new-batch-btn" onClick={() => setNewBatchOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md text-sm">
              <Plus size={14} /> Add Stock
            </button>
          )}
          {(role === "manufacturer" || role === "distributor") && (
            <button data-testid="allocate-btn"
              onClick={() => setAllocOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-sm">
              <Send size={14} /> Allocate{selectedCodes.length > 0 ? ` (${selectedCodes.length})` : ""}
            </button>
          )}
        </div>
      </div>

      <SummaryCards summary={summary} />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex bg-[#FFFFFF] border border-[#E5E7EB] rounded-md p-1">
          <button onClick={() => setTab("products")} data-testid="tab-products"
            className={`px-4 py-1.5 text-sm rounded-md ${tab === "products" ? "bg-[#E63946] text-white" : "text-[#475569]"}`}>Master Products</button>
          <button onClick={() => setTab("items")} data-testid="tab-items"
            className={`px-4 py-1.5 text-sm rounded-md ${tab === "items" ? "bg-[#E63946] text-white" : "text-[#475569]"}`}>PPF Items</button>
        </div>
        {tab === "items" && (
          <>
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <input
                data-testid="stock-search"
                value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search PPF code..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] rounded-md font-mono text-slate-900 focus:outline-none focus:border-[#E63946]"
              />
            </div>
            <select data-testid="stock-state-filter" value={state} onChange={(e) => setState(e.target.value)}
              className="px-3 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none">
              <option value="">All States</option>
              <option value="available">Available</option>
              <option value="allocated">Allocated</option>
              <option value="consumed">Consumed</option>
            </select>
          </>
        )}
      </div>

      {loading ? <div className="text-sm text-[#475569]">Loading…</div> :
        tab === "products" ? (
          <ProductsView
            products={productsGrouped}
            expanded={expandedProduct}
            toggle={toggleProduct}
            rolls={productRolls}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            role={role}
            onDeleteRoll={deleteRoll}
            onDeleteMaster={deleteMasterProduct}
            onTransferRoll={setTransferRoll}
            onEditRoll={setEditRoll}
            onUnallocateRoll={unallocateRoll}
          />
        ) : (
          <ItemsTable items={items} selectedItems={selectedItems} setSelectedItems={setSelectedItems} role={role}
            onDeleteRoll={deleteRoll} onTransferRoll={setTransferRoll}
            onEditRoll={setEditRoll} onUnallocateRoll={unallocateRoll} />
        )
      }

      {newBatchOpen && <NewBatchModal products={products} onClose={() => setNewBatchOpen(false)} onDone={() => { setNewBatchOpen(false); setProductRolls({}); load(); }} />}
      {transferRoll && (
        <TransferRollModal
          roll={transferRoll}
          onClose={() => setTransferRoll(null)}
          onDone={() => { setTransferRoll(null); setProductRolls({}); load(); }}
        />
      )}
      {editRoll && (
        <EditRollModal
          roll={editRoll} products={products}
          onClose={() => setEditRoll(null)}
          onDone={() => { setEditRoll(null); setProductRolls({}); load(); }}
        />
      )}
      {allocOpen && (
        <AllocateModal
          codes={selectedCodes} role={role}
          onClose={() => setAllocOpen(false)}
          onDone={() => { setAllocOpen(false); setSelectedItems({}); setProductRolls({}); load(); }}
        />
      )}
    </div>
  );
}

function SummaryCards({ summary }) {
  const entries = Object.entries(summary || {});
  if (entries.length === 0) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {entries.map(([k, v]) => (
        <div key={k} className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-5" data-testid={`stock-summary-${k}`}>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B]">{k.replace(/_/g, " ")}</div>
          <div className="text-2xl font-medium mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function ProductsView({ products, expanded, toggle, rolls, selectedItems, setSelectedItems, role, onDeleteRoll, onDeleteMaster, onTransferRoll, onEditRoll, onUnallocateRoll }) {
  if (!products || products.length === 0) return <Empty>No stock yet. Add rolls to get started.</Empty>;
  return (
    <div className="space-y-3" data-testid="products-view">
      {products.map((p) => {
        const isOpen = expanded === p.product_name;
        const productRolls = rolls[p.product_name] || [];
        const slug = p.product_name.replace(/\s+/g, "-").toLowerCase();
        return (
          <div key={p.product_name} className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] overflow-hidden"
            data-testid={`product-card-${slug}`}>
            <div className="w-full flex items-center gap-4 px-5 py-4 hover:bg-[#F8FAFC] text-left">
              <button
                onClick={() => toggle(p.product_name)}
                data-testid={`product-toggle-${slug}`}
                className="flex items-center gap-4 flex-1"
              >
                <div className="w-9 h-9 rounded-lg bg-[#E63946]/10 text-[#E63946] flex items-center justify-center">
                  <Package size={16} />
                </div>
                <div className="flex-1 text-left">
                  <div className="font-medium text-slate-900">{p.product_name}</div>
                  <div className="text-xs text-[#64748B] mt-0.5">{p.total} roll(s) • {p.total_sqft?.toLocaleString?.() || p.total_sqft} SqFt total • {p.remaining_sqft?.toLocaleString?.() || p.remaining_sqft} SqFt remaining</div>
                </div>
                <div className="hidden md:flex items-center gap-6 text-xs">
                  <Metric label="Total" value={p.total} />
                  {role === "manufacturer" && <Metric label="At Mfr." value={p.at_manufacturer} />}
                  <Metric label="Allocated" value={p.allocated} tone="amber" />
                  <Metric label="Consumed" value={p.consumed} tone="slate" />
                </div>
                {isOpen ? <ChevronDown size={18} className="text-[#64748B]" /> : <ChevronRight size={18} className="text-[#64748B]" />}
              </button>
              {role === "manufacturer" && (
                <button data-testid={`delete-master-${slug}`}
                  onClick={(e) => { e.stopPropagation(); onDeleteMaster(p.product_name); }}
                  title={`Delete all unallocated ${p.product_name} rolls`}
                  className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><Trash2 size={14} /></button>
              )}
            </div>
            <div className="md:hidden grid grid-cols-4 gap-2 px-5 pb-3 text-center">
              <Metric label="Total" value={p.total} compact />
              {role === "manufacturer" && <Metric label="At Mfr" value={p.at_manufacturer} compact />}
              <Metric label="Alloc" value={p.allocated} tone="amber" compact />
              <Metric label="Used" value={p.consumed} tone="slate" compact />
            </div>
            {isOpen && (
              <div className="border-t border-[#E5E7EB] bg-[#F8FAFC]/40" data-testid={`product-rolls-${slug}`}>
                {productRolls.length === 0 ? (
                  <div className="px-5 py-6 text-sm text-[#64748B] text-center">Loading rolls…</div>
                ) : (
                  <ItemsTable
                    items={productRolls}
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    role={role}
                    embedded
                    onDeleteRoll={onDeleteRoll}
                    onTransferRoll={onTransferRoll}
                    onEditRoll={onEditRoll}
                    onUnallocateRoll={onUnallocateRoll}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Metric({ label, value, tone, compact }) {
  const tones = { amber: "text-[#F59E0B]", slate: "text-[#64748B]" };
  return (
    <div className={compact ? "" : "text-right"}>
      <div className="text-[9px] uppercase tracking-[0.16em] text-[#64748B]">{label}</div>
      <div className={`font-mono font-medium ${tones[tone] || "text-slate-900"} ${compact ? "text-sm" : "text-base"}`}>{value ?? 0}</div>
    </div>
  );
}

function HeldByCell({ item }) {
  const role = item.current_owner_role;
  if (role === "dealer") {
    // Dealer stock — show both distributor and dealer names
    return (
      <div className="text-xs">
        <div className="text-slate-900">{item.dealer_name || "—"}</div>
        <div className="text-[10px] text-[#64748B]">via {item.distributor_name || "—"}</div>
      </div>
    );
  }
  if (role === "distributor") {
    return <div className="text-xs text-slate-900">{item.distributor_name || "—"}</div>;
  }
  return <div className="text-xs text-[#64748B]">Manufacturer</div>;
}

function ItemsTable({ items, selectedItems, setSelectedItems, role, embedded, onDeleteRoll, onTransferRoll, onEditRoll, onUnallocateRoll }) {
  if (items.length === 0) return <Empty>No stock items match the filter.</Empty>;
  const canSelect = role === "manufacturer" || role === "distributor";
  const canManage = role === "manufacturer";
  const toggle = (code) => setSelectedItems({ ...selectedItems, [code]: !selectedItems[code] });
  return (
    <div className={embedded ? "overflow-hidden" : "rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] overflow-hidden"}>
      <table className="w-full text-sm" data-testid="items-table">
        <thead><tr className="border-b border-[#E5E7EB] text-left">
          {canSelect && <th className="px-3 py-3 w-8"></th>}
          {["PPF Code", "Product", "SqFt", "State", "Held By", "Created"].map(h =>
            <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{h}</th>)}
          {canManage && <th className="px-3 py-3 w-40 text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">Actions</th>}
        </tr></thead>
        <tbody>{items.map(i => (
          <tr key={i.id} className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC]">
            {canSelect && (
              <td className="px-3 py-3">
                <input type="checkbox"
                  data-testid={`select-${i.ppf_code}`}
                  checked={!!selectedItems[i.ppf_code]}
                  onChange={() => toggle(i.ppf_code)}
                  disabled={i.state === "consumed"}
                  className="accent-[#E63946]"
                />
              </td>
            )}
            <td className="px-4 py-3 font-mono text-[#E63946]">{i.ppf_code}</td>
            <td className="px-4 py-3">{i.product_name}</td>
            <td className="px-4 py-3 font-mono text-xs">{i.roll_size_sqft ?? "-"} / {i.remaining_sqft ?? 0}</td>
            <td className="px-4 py-3"><StatePill state={i.state} /></td>
            <td className="px-4 py-3"><HeldByCell item={i} /></td>
            <td className="px-4 py-3 text-xs text-[#64748B]">{fmt(i.created_at)}</td>
            {canManage && (
              <td className="px-3 py-3">
                <div className="flex gap-1">
                  {i.current_owner_role === "manufacturer" && i.state !== "consumed" && (
                    <button data-testid={`edit-roll-${i.ppf_code}`}
                      onClick={() => onEditRoll?.(i)}
                      className="p-1.5 text-[#0EA5E9] hover:bg-[#0EA5E9]/10 rounded"
                      title="Edit this roll"><Pencil size={13} /></button>
                  )}
                  {i.state !== "consumed" && (
                    <button data-testid={`transfer-roll-${i.ppf_code}`}
                      onClick={() => onTransferRoll?.(i)}
                      className="p-1.5 text-[#E63946] hover:bg-[#E63946]/10 rounded"
                      title="Transfer this roll"><Send size={13} /></button>
                  )}
                  {i.current_owner_role !== "manufacturer" && i.state !== "consumed" && (
                    <button data-testid={`unallocate-roll-${i.ppf_code}`}
                      onClick={() => onUnallocateRoll?.(i.id, i.ppf_code)}
                      className="p-1.5 text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded"
                      title="Recall to Manufacturer"><Undo2 size={13} /></button>
                  )}
                  {i.current_owner_role === "manufacturer" && i.state !== "consumed" && (
                    <button data-testid={`delete-roll-${i.ppf_code}`}
                      onClick={() => onDeleteRoll?.(i.id, i.ppf_code)}
                      className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"
                      title="Delete this roll"><Trash2 size={13} /></button>
                  )}
                </div>
              </td>
            )}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function StatePill({ state }) {
  const cm = { available: "#10B981", allocated: "#F59E0B", partially_used: "#F59E0B", consumed: "#64748B" };
  const c = cm[state] || "#64748B";
  return <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em]"
    style={{ background: c + "22", color: c, border: `1px solid ${c}55` }}>{state}</span>;
}

function Empty({ children }) { return <div className="text-center py-16 text-[#64748B] border border-dashed border-[#E5E7EB] rounded-xl">{children}</div>; }
function fmt(d) { if (!d) return "-"; try { return new Date(d).toLocaleDateString("en-IN"); } catch { return d; } }

function NewBatchModal({ products, onClose, onDone }) {
  const [rolls, setRolls] = useState([{ roll_no: "", product_id: "", roll_size_sqft: 300 }]);
  const [csv, setCsv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [distributors, setDistributors] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [distributorUserId, setDistributorUserId] = useState("");
  const [dealerUserId, setDealerUserId] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/distributors?status=approved").then(r => r.data || []).catch(() => []),
      api.get("/dealers").then(r => r.data || []).catch(() => []),
    ]).then(([dists, dlrs]) => {
      setDistributors(dists);
      setDealers(dlrs);
    });
  }, []);

  // When user picks a dealer, auto-populate distributor
  const onDealerChange = (val) => {
    setDealerUserId(val);
    if (val) {
      const d = dealers.find(x => x.user_id === val);
      if (d?.distributor_id) setDistributorUserId(d.distributor_id);
    }
  };

  // When user changes distributor, clear dealer if it belongs to a different distributor
  const onDistributorChange = (val) => {
    setDistributorUserId(val);
    if (dealerUserId) {
      const d = dealers.find(x => x.user_id === dealerUserId);
      if (d && d.distributor_id !== val) setDealerUserId("");
    }
  };

  const addRow = () => setRolls([...rolls, {
    roll_no: "",
    product_id: rolls[rolls.length - 1]?.product_id || "",
    roll_size_sqft: rolls[rolls.length - 1]?.roll_size_sqft || 300,
  }]);
  const removeRow = (i) => setRolls(rolls.filter((_, ix) => ix !== i));
  const updateRow = (i, field, val) => {
    const next = [...rolls]; next[i] = { ...next[i], [field]: val }; setRolls(next);
  };

  const submitRolls = async () => {
    const clean = rolls
      .filter(r => r.roll_no.trim() && r.product_id && r.roll_size_sqft)
      .map(r => ({ ...r, roll_size_sqft: Number(r.roll_size_sqft) }));
    if (clean.length === 0) { toast.error("Add at least one complete row (Roll No, Product & SqFt)"); return; }
    setBusy(true);
    try {
      const payload = { rolls: clean };
      if (dealerUserId) payload.dealer_user_id = dealerUserId;
      else if (distributorUserId) payload.distributor_user_id = distributorUserId;
      const r = await api.post("/stock/add-rolls", payload);
      setResult(r.data);
      if (r.data.failed.length === 0) {
        const allocMsg = r.data.allocated ? ` and allocated ${r.data.allocated}` : "";
        toast.success(`${r.data.inserted} roll(s) added${allocMsg}`);
        onDone();
      } else {
        toast.warning(`${r.data.inserted} added, ${r.data.failed.length} failed`);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submitCsv = async () => {
    if (!csv) { toast.error("Select a CSV file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", csv);
      if (dealerUserId) fd.append("dealer_user_id", dealerUserId);
      else if (distributorUserId) fd.append("distributor_user_id", distributorUserId);
      const r = await api.post("/stock/bulk-upload-rolls", fd);
      setResult(r.data);
      const allocMsg = r.data.allocated ? ` and allocated ${r.data.allocated}` : "";
      toast.success(`${r.data.inserted} rolls imported (${r.data.failed} failed)${allocMsg}`);
      if (r.data.failed === 0) onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const downloadSample = () => {
    const token = localStorage.getItem("cpl_token");
    fetch(`${API_BASE}/stock/bulk-rolls-sample-csv`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.blob()).then(b => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = url; a.download = "stock_rolls_sample.csv"; a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <Modal title="Add Stock Rolls" onClose={onClose}>
      <div className="space-y-4">
        <div className="text-xs text-[#64748B]">Each row is one roll. Select roll size (200/250/300 SqFt) per row.</div>
        <div className="space-y-2">
          {rolls.map((r, i) => (
            <div key={i} className="flex items-start gap-2" data-testid={`roll-row-${i}`}>
              <div className="flex-1">
                <input placeholder="Roll No (e.g. CPL-001)"
                  data-testid={`roll-no-${i}`}
                  value={r.roll_no} onChange={(e) => updateRow(i, "roll_no", e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md font-mono text-sm focus:outline-none focus:border-[#E63946]" />
              </div>
              <div className="flex-1">
                <select data-testid={`roll-product-${i}`} value={r.product_id}
                  onChange={(e) => updateRow(i, "product_id", e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#E63946]">
                  <option value="">Select product…</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.category} • {p.name}</option>)}
                </select>
              </div>
              <div className="w-28">
                <select data-testid={`roll-sqft-${i}`} value={r.roll_size_sqft}
                  onChange={(e) => updateRow(i, "roll_size_sqft", e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#E63946]">
                  {ROLL_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s} SqFt</option>)}
                </select>
              </div>
              {rolls.length > 1 && (
                <button type="button" onClick={() => removeRow(i)} data-testid={`remove-roll-${i}`}
                  className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><Trash2 size={14} /></button>
              )}
            </div>
          ))}
        </div>
        <button type="button" data-testid="add-more-row" onClick={addRow}
          className="w-full py-2 border border-dashed border-[#E5E7EB] text-[#64748B] hover:text-slate-900 hover:bg-[#F8FAFC] rounded-md text-sm inline-flex items-center justify-center gap-1">
          <Plus size={14} /> Add more
        </button>
        <div className="border-t border-[#E5E7EB] pt-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">
              Allocate to Distributor (optional)
            </label>
            <select data-testid="add-rolls-distributor" value={distributorUserId}
              onChange={(e) => onDistributorChange(e.target.value)}
              className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#E63946]">
              <option value="">Keep at Manufacturer (no allocation)</option>
              {distributors.map(d => (
                <option key={d.user_id} value={d.user_id}>{d.name} ({d.address_city})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">
              Allocate directly to Dealer (optional)
            </label>
            <select data-testid="add-rolls-dealer" value={dealerUserId}
              onChange={(e) => onDealerChange(e.target.value)}
              className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#E63946]">
              <option value="">— None —</option>
              {dealers
                .filter(d => !distributorUserId || d.distributor_id === distributorUserId)
                .map(d => (
                  <option key={d.user_id} value={d.user_id}>{d.name} ({d.address_city})</option>
                ))}
            </select>
            <div className="text-[11px] text-[#64748B] mt-1">
              If a dealer is chosen, rolls go straight to the dealer (distributor auto-derived; distributor can view but can’t re-allocate).
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#F8FAFC]">Cancel</button>
          <button data-testid="add-rolls-submit" onClick={submitRolls} disabled={busy}
            className="px-5 py-2 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md text-sm font-medium disabled:opacity-60">
            {busy ? "Adding..." : "Add Rolls"}
          </button>
        </div>
        <div className="border-t border-[#E5E7EB] pt-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B] mb-2">
            Or Bulk Upload CSV (roll_no, product_name, roll_size_sqft)
          </div>
          <div className="flex items-center gap-2">
            <input type="file" accept=".csv,.txt" data-testid="rolls-csv" onChange={(e) => setCsv(e.target.files?.[0])}
              className="text-sm text-[#64748B] file:mr-3 file:px-3 file:py-1.5 file:rounded file:bg-white file:text-slate-900 file:border file:border-[#E5E7EB] file:cursor-pointer" />
            {csv && <button onClick={submitCsv} disabled={busy} data-testid="submit-csv"
              className="px-4 py-1.5 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded text-xs disabled:opacity-60">Upload</button>}
          </div>
          <button type="button" onClick={downloadSample} data-testid="download-rolls-sample"
            className="mt-2 text-xs text-[#E63946] hover:underline inline-flex items-center gap-1">
            <Upload size={12} className="rotate-180" /> Download sample CSV
          </button>
        </div>
        {result && result.failed && (Array.isArray(result.failed) ? result.failed.length > 0 : result.failed > 0) && (
          <div className="rounded p-3 bg-[#FEF2F2] border border-[#EF4444]/30 max-h-40 overflow-auto">
            <div className="text-xs text-[#EF4444] font-medium mb-1">Errors:</div>
            {(Array.isArray(result.failed) ? result.failed : result.errors || []).map((e, i) => (
              <div key={i} className="text-[10px] font-mono text-[#EF4444]">{e.roll_no || `Row ${e.row}`}: {e.error}</div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function AllocateModal({ codes, role, onClose, onDone }) {
  const [targets, setTargets] = useState([]);
  const [to_user_id, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [foundRolls, setFoundRolls] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const to_role = role === "manufacturer" ? "distributor" : "dealer";

  useEffect(() => {
    const init = {};
    (codes || []).forEach(c => { init[c] = true; });
    setSelected(init);
  }, [codes]);

  useEffect(() => {
    (async () => {
      const url = role === "manufacturer" ? "/distributors?status=approved" : "/dealers";
      const r = await api.get(url);
      setTargets(r.data.map(d => ({ id: d.user_id, label: `${d.name} (${d.address_city})` })));
    })();
  }, [role]);

  const searchRolls = async () => {
    const query = (search || "").trim();
    if (!query) { toast.error("Enter a Roll No to search"); return; }
    try {
      const state = role === "manufacturer" ? "available" : "";
      const r = await api.get(`/stock/items?q=${encodeURIComponent(query)}${state ? `&state=${state}` : ""}`);
      const rows = (r.data || []).filter(x =>
        role === "manufacturer"
          ? x.state === "available" && x.current_owner_role === "manufacturer"
          : x.current_owner_role === "distributor"
      );
      setFoundRolls(rows);
      if (rows.length === 0) toast.warning("No matching available roll found");
    } catch { toast.error("Search failed"); }
  };

  const toggle = (code) => setSelected({ ...selected, [code]: !selected[code] });
  const selectedList = Object.keys(selected).filter(k => selected[k]);

  const submit = async () => {
    if (!to_user_id) { toast.error("Select target " + to_role); return; }
    if (selectedList.length === 0) { toast.error("Select at least one roll"); return; }
    setBusy(true);
    try {
      const r = await api.post("/stock/allocate", { ppf_codes: selectedList, to_user_id, to_role });
      toast.success(`Allocated ${r.data.allocated} roll(s)`);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Allocate Rolls to ${to_role}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Target {to_role}</label>
          <select data-testid="alloc-target" value={to_user_id} onChange={(e) => setTo(e.target.value)}
            className="mt-1 w-full px-3 py-2.5 bg-white border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]">
            <option value="">Select…</option>
            {targets.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Search Roll No</label>
          <div className="mt-1 flex gap-2">
            <input data-testid="alloc-roll-search" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchRolls()}
              placeholder="Enter roll no..."
              className="flex-1 px-3 py-2 bg-white border border-[#E5E7EB] rounded-md font-mono text-sm focus:outline-none focus:border-[#E63946]" />
            <button data-testid="alloc-search-btn" onClick={searchRolls}
              className="px-4 py-2 text-sm bg-white border border-[#E5E7EB] hover:bg-[#F8FAFC] rounded-md">
              Search
            </button>
          </div>
        </div>
        {foundRolls.length > 0 && (
          <div className="rounded-md border border-[#E5E7EB] overflow-hidden max-h-56 overflow-y-auto">
            <table className="w-full text-xs" data-testid="alloc-found-table">
              <thead className="bg-[#F8FAFC]"><tr>
                {["", "Roll No", "Product", "State"].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] uppercase text-[#64748B]">{h}</th>)}
              </tr></thead>
              <tbody>{foundRolls.map(r => (
                <tr key={r.ppf_code} className="border-t border-[#E5E7EB] hover:bg-[#F8FAFC]">
                  <td className="px-3 py-2">
                    <input type="checkbox" data-testid={`alloc-select-${r.ppf_code}`}
                      checked={!!selected[r.ppf_code]} onChange={() => toggle(r.ppf_code)}
                      className="accent-[#E63946]" />
                  </td>
                  <td className="px-3 py-2 font-mono text-[#E63946]">{r.ppf_code}</td>
                  <td className="px-3 py-2">{r.product_name}</td>
                  <td className="px-3 py-2">{r.state}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {selectedList.length > 0 && (
          <div className="rounded-md bg-[#F8FAFC] border border-[#E5E7EB] px-3 py-2 text-xs">
            <span className="text-[#64748B]">Selected: </span>
            <span className="font-mono text-[#E63946]">{selectedList.length} roll(s)</span> — {selectedList.slice(0, 5).join(", ")}{selectedList.length > 5 ? "..." : ""}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#F8FAFC]">Cancel</button>
          <button data-testid="alloc-submit" onClick={submit} disabled={busy || selectedList.length === 0}
            className="px-5 py-2 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md text-sm font-medium disabled:opacity-50">
            {busy ? "Allocating..." : `Allocate ${selectedList.length} Roll(s)`}
          </button>
        </div>
      </div>
    </Modal>
  );
}


function EditRollModal({ roll, products, onClose, onDone }) {
  const [ppfCode, setPpfCode] = useState(roll.ppf_code || "");
  const [productId, setProductId] = useState(roll.product_id || "");
  const [sqft, setSqft] = useState(roll.roll_size_sqft || 300);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (ppfCode && ppfCode !== roll.ppf_code) payload.ppf_code = ppfCode;
      if (productId && productId !== roll.product_id) payload.product_id = productId;
      if (Number(sqft) !== roll.roll_size_sqft) payload.roll_size_sqft = Number(sqft);
      if (Object.keys(payload).length === 0) { toast.error("No changes to save"); return; }
      await api.patch(`/stock/rolls/${roll.id}`, payload);
      toast.success("Roll updated");
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Edit Roll ${roll.ppf_code}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="text-xs text-[#64748B]">
          Only rolls currently at Manufacturer can be edited. Unallocate first if needed.
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Roll No</label>
          <input data-testid="edit-ppf-code" value={ppfCode} onChange={e => setPpfCode(e.target.value)}
            className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md font-mono text-sm focus:outline-none focus:border-[#E63946]" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Product</label>
          <select data-testid="edit-product" value={productId} onChange={e => setProductId(e.target.value)}
            className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#E63946]">
            {products.map(p => <option key={p.id} value={p.id}>{p.category} • {p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Roll Size (SqFt)</label>
          <select data-testid="edit-sqft" value={sqft} onChange={e => setSqft(e.target.value)}
            className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#E63946]">
            {ROLL_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s} SqFt</option>)}
          </select>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#F8FAFC]">Cancel</button>
          <button data-testid="edit-submit" onClick={submit} disabled={busy}
            className="px-5 py-2 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md text-sm font-medium disabled:opacity-50">
            {busy ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function TransferRollModal({ roll, onClose, onDone }) {
  const [toRole, setToRole] = useState("distributor");
  const [toUserId, setToUserId] = useState("");
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const url = toRole === "distributor" ? "/distributors?status=approved" : "/dealers";
    api.get(url).then(r => setOptions(r.data || [])).catch(() => setOptions([]));
    setToUserId("");
  }, [toRole]);

  const filtered = options.filter(o => {
    const s = query.toLowerCase();
    return !s || (o.name || "").toLowerCase().includes(s) ||
           (o.address_city || "").toLowerCase().includes(s) ||
           (o.phone || "").includes(s);
  });

  const submit = async () => {
    if (!toUserId) { toast.error(`Choose a ${toRole}`); return; }
    setBusy(true);
    try {
      await api.patch(`/stock/rolls/${roll.id}/transfer`, { to_role: toRole, to_user_id: toUserId });
      toast.success(`Transferred ${roll.ppf_code} to ${toRole}`);
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Transfer failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Transfer Roll ${roll.ppf_code}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="text-xs text-[#64748B]">
          Currently held by <b className="text-slate-900">{roll.current_owner_role}</b>
          {roll.distributor_name && ` (${roll.dealer_name || roll.distributor_name})`}.
        </div>
        <div className="flex gap-2">
          {["distributor", "dealer"].map(r => (
            <button key={r} type="button"
              data-testid={`transfer-role-${r}`}
              onClick={() => setToRole(r)}
              className={`flex-1 py-2 rounded-md border text-sm capitalize ${
                toRole === r ? "bg-[#E63946] text-white border-[#E63946]" : "bg-white border-[#E5E7EB] text-[#475569]"
              }`}>{r}</button>
          ))}
        </div>
        <div>
          <input
            data-testid="transfer-search"
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${toRole} by name, city, phone...`}
            className="w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:border-[#E63946]"
          />
          <div className="mt-2 max-h-60 overflow-y-auto rounded-md border border-[#E5E7EB]">
            {filtered.length === 0 ? (
              <div className="text-center py-6 text-xs text-[#64748B]">No matches.</div>
            ) : filtered.map(o => (
              <label key={o.user_id}
                data-testid={`transfer-option-${o.user_id}`}
                className={`flex items-center gap-3 px-3 py-2 border-b border-[#E5E7EB] last:border-b-0 cursor-pointer hover:bg-[#F8FAFC] ${
                  toUserId === o.user_id ? "bg-[#E63946]/5" : ""
                }`}>
                <input type="radio" name="target"
                  checked={toUserId === o.user_id} onChange={() => setToUserId(o.user_id)}
                  className="accent-[#E63946]" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{o.name}</div>
                  <div className="text-[11px] text-[#64748B]">{o.address_city}, {o.address_state} · +91 {o.phone}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#F8FAFC]">Cancel</button>
          <button data-testid="transfer-submit" onClick={submit} disabled={busy || !toUserId}
            className="px-5 py-2 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md text-sm font-medium disabled:opacity-50">
            {busy ? "Transferring..." : "Transfer"}
          </button>
        </div>
      </div>
    </Modal>
  );
}


function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="text-lg font-medium">{title}</h3>
          <button onClick={onClose} className="text-[#475569] hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
